import React from 'react';
import { CheckCircle2 } from 'lucide-react';

interface ToastProps {
  /**
   * When non-null, the toast is visible and displays `message`. The caller is
   * responsible for clearing this (typically `setTimeout` 2000ms after
   * `lastSavedAt` changes).
   */
  message: string | null;
}

/**
 * Tiny green "saved" pill for the resource editor. Intentionally has no
 * timer of its own — U8 mandates 2s auto-dismiss driven by `lastSavedAt`,
 * so keeping that responsibility in the caller avoids double-timers.
 *
 * Renders `null` when no message is set (rather than a pinned-invisible
 * element) so it doesn't occupy flow space above the first section. The
 * previous `opacity-0 pointer-events-none` approach kept the element in
 * the DOM and added a hidden margin above the Alert / first section.
 */
export const Toast: React.FC<ToastProps> = ({ message }) => {
  if (message === null) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium opacity-100"
    >
      <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
      <span>{message}</span>
    </div>
  );
};
